import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Plus, Eye, Trash2, Loader2, CalendarClock, MailOpen, MousePointerClick, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const FLAGS = { CH: "🇨🇭", DE: "🇩🇪", AT: "🇦🇹" };

const STATUS = {
    draft: { label: "Entwurf", className: "bg-slate-100 text-slate-600 border-0" },
    scheduled: { label: "Geplant", className: "bg-amber-100 text-amber-700 border-0" },
    sending: { label: "Wird gesendet", className: "bg-blue-100 text-blue-700 border-0" },
    sent: { label: "Gesendet", className: "bg-emerald-100 text-emerald-700 border-0" },
    failed: { label: "Fehler", className: "bg-rose-100 text-rose-700 border-0" },
};

export default function CampaignsPage() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [previewLang, setPreviewLang] = useState("DE");
    const [form, setForm] = useState({
        name: "",
        description: "",
        batch_size: 20,
        target_filter: { countries: [], languages: [] },
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/campaigns");
            setCampaigns(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) { toast.error("Bitte einen Namen eingeben"); return; }
        try {
            const payload = { ...form, batch_size: form.batch_size > 0 ? Number(form.batch_size) : null };
            await api.post("/campaigns", payload);
            toast.success("Kampagne erstellt");
            setCreateOpen(false);
            setForm({ name: "", description: "", batch_size: 20, exclude_already_emailed: true, target_filter: { countries: [], languages: [] } });
            load();
        } catch { toast.error("Erstellung fehlgeschlagen"); }
    };

    const openPreview = async (campaign) => {
        setSelectedCampaign(campaign);
        setPreviewData(null);
        setPreviewOpen(true);
        try {
            const { data } = await api.post(`/campaigns/${campaign.id}/preview`);
            setPreviewData(data);
            if (data.previews?.length) setPreviewLang(data.previews[0].language);
        } catch { toast.error("Vorschau konnte nicht geladen werden"); }
    };

    const send = async (id) => {
        try {
            const { data } = await api.post(`/campaigns/${id}/send`);
            const remaining = data.remaining_after || 0;
            toast.success(
                `Versand gestartet — ${data.sending_count} E-Mails werden gesendet${remaining > 0 ? ` (${remaining} verbleibend für nächsten Batch)` : ""}`
            );
            setPreviewOpen(false);
            load();
            setTimeout(load, 4000);
            setTimeout(load, 12000);
            setTimeout(load, 25000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Kampagne löschen?")) return;
        try { await api.delete(`/campaigns/${id}`); load(); toast.success("Gelöscht"); }
        catch { toast.error("Fehler"); }
    };

    const toggleCountry = (c) => {
        const list = form.target_filter.countries || [];
        const next = list.includes(c) ? list.filter((x) => x !== c) : [...list, c];
        setForm({ ...form, target_filter: { ...form.target_filter, countries: next } });
    };
    const toggleLang = (l) => {
        const list = form.target_filter.languages || [];
        const next = list.includes(l) ? list.filter((x) => x !== l) : [...list, l];
        setForm({ ...form, target_filter: { ...form.target_filter, languages: next } });
    };

    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="campaigns-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Outreach</div>
                    <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Kampagnen.</h1>
                    <p className="text-slate-500 mt-3 max-w-xl">Vorlagen + Empfänger = Kampagne. Mehrsprachig, automatisiert, messbar.</p>
                </div>
                <Button data-testid="create-campaign-btn" onClick={() => setCreateOpen(true)}
                    className="bg-[#007bff] hover:bg-[#0056b3] h-11 px-5 shadow-md shadow-blue-500/20">
                    <Plus className="w-4 h-4 mr-2" /> Neue Kampagne
                </Button>
            </div>

            <Card className="bg-white border-slate-200 rounded-xl overflow-hidden">
                {loading ? <div className="p-12 text-center text-slate-400">Lädt ...</div>
                    : campaigns.length === 0 ? (
                        <div className="p-16 text-center">
                            <Send className="w-12 h-12 text-slate-200 mx-auto mb-4" strokeWidth={1.3} />
                            <h3 className="font-display text-2xl text-[#1a2b48]">Noch keine Kampagnen.</h3>
                            <p className="text-slate-500 mt-2 text-sm">Erstellen Sie Ihre erste Outreach-Kampagne.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100" data-testid="campaigns-list">
                            {campaigns.map((c) => {
                                const totalSent = c.stats?.sent || 0;
                                const totalRecipients = c.stats?.total || 0;
                                const remaining = Math.max(0, totalRecipients - totalSent);
                                const batchSize = c.batch_size || 0;
                                const nextBatch = batchSize > 0 ? Math.min(batchSize, remaining) : remaining;
                                const canSendMore = remaining > 0 && c.status !== "sending";
                                return (
                                <div key={c.id} className="p-5 hover:bg-slate-50/40 transition-colors flex flex-wrap items-center gap-4 justify-between" data-testid={`campaign-row-${c.id}`}>
                                    <div className="flex-1 min-w-[240px]">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-medium text-[#1a2b48]">{c.name}</span>
                                            <Badge className={STATUS[c.status]?.className}>{STATUS[c.status]?.label}</Badge>
                                            {c.target_filter?.countries?.length > 0 && (
                                                <span className="text-xs text-slate-500">
                                                    {c.target_filter.countries.map((cc) => FLAGS[cc]).join(" ")}
                                                </span>
                                            )}
                                            {batchSize > 0 && (
                                                <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                                                    Batch {batchSize}
                                                </Badge>
                                            )}
                                        </div>
                                        {c.description && <div className="text-xs text-slate-500 mt-1">{c.description}</div>}
                                        <div className="flex gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                                            <span><strong className="text-[#1a2b48]">{totalRecipients}</strong> Empfänger</span>
                                            <span><strong className="text-emerald-600">{totalSent}</strong> gesendet</span>
                                            <span><strong className="text-amber-600">{remaining}</strong> verbleibend</span>
                                            <span><strong className="text-[#007bff]">{c.stats.opened || 0}</strong> geöffnet</span>
                                            {c.stats.failed > 0 && <span className="text-rose-600"><strong>{c.stats.failed}</strong> fehlgeschlagen</span>}
                                        </div>
                                        {totalRecipients > 0 && (
                                            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-md">
                                                <div className="h-full bg-emerald-500 transition-all"
                                                    style={{ width: `${Math.min(100, (totalSent / totalRecipients) * 100)}%` }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openPreview(c)} data-testid={`preview-campaign-${c.id}`}>
                                            <Eye className="w-4 h-4 mr-1.5" />Vorschau
                                        </Button>
                                        {canSendMore && (
                                            <Button size="sm" onClick={() => send(c.id)} data-testid={`send-campaign-${c.id}`}
                                                className="bg-[#007bff] hover:bg-[#0056b3]">
                                                <Send className="w-4 h-4 mr-1.5" />
                                                {totalSent > 0 ? `Nächste ${nextBatch} senden` : (batchSize > 0 ? `${nextBatch} senden` : "Senden")}
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="icon" onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-500">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
            </Card>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-lg" data-testid="create-campaign-dialog">
                    <DialogHeader><DialogTitle className="font-display text-2xl text-[#1a2b48]">Neue Kampagne</DialogTitle></DialogHeader>
                    <div className="space-y-5 py-2">
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Name *</Label>
                            <Input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Frühjahrs-Outreach DACH" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Beschreibung</Label>
                            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Anzahl pro Versand (Batch-Größe)</Label>
                            <Input data-testid="campaign-batch-input" type="number" min="0" value={form.batch_size}
                                onChange={(e) => setForm({ ...form, batch_size: e.target.value === "" ? "" : Number(e.target.value) })}
                                placeholder="z.B. 20" />
                            <p className="text-[11px] text-slate-500 leading-relaxed">
                                Sendet pro Klick auf "Senden" maximal so viele E-Mails. <strong>0 = alle auf einmal</strong>.
                                Mehrfach klicken über Tage hinweg → jedesmal die nächsten N (bereits angeschriebene werden übersprungen).
                                <br/>Empfohlen: <strong>20-50/Tag</strong> für gute Sender-Reputation.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Zielgruppe — Länder (leer = alle)</Label>
                            <div className="flex gap-2 flex-wrap">
                                {["CH", "DE", "AT"].map((c) => {
                                    const active = form.target_filter.countries.includes(c);
                                    return (
                                        <button key={c} type="button" onClick={() => toggleCountry(c)} data-testid={`target-country-${c}`}
                                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${active ? "bg-[#1a2b48] text-white border-[#1a2b48]" : "bg-white border-slate-200 text-slate-600 hover:border-[#007bff]"}`}>
                                            {FLAGS[c]} {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider text-slate-600">Sprachen (leer = alle)</Label>
                            <div className="flex gap-2 flex-wrap">
                                {["DE", "EN", "FR", "IT"].map((l) => {
                                    const active = form.target_filter.languages.includes(l);
                                    return (
                                        <button key={l} type="button" onClick={() => toggleLang(l)} data-testid={`target-lang-${l}`}
                                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${active ? "bg-[#007bff] text-white border-[#007bff]" : "bg-white border-slate-200 text-slate-600 hover:border-[#007bff]"}`}>
                                            {l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.exclude_already_emailed}
                                    onChange={(e) => setForm({ ...form, exclude_already_emailed: e.target.checked })}
                                    data-testid="exclude-emailed-checkbox"
                                    className="mt-1 w-4 h-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500"
                                />
                                <div className="flex-1">
                                    <div className="font-semibold text-emerald-900 text-sm">
                                        Nur an noch nie angeschriebene Reisebüros senden
                                    </div>
                                    <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                                        Empfohlen ✅ — überspringt automatisch alle Kontakte, die bereits eine E-Mail bekommen haben (in dieser oder einer früheren Kampagne).
                                        Perfekt um neue Kontaktlisten ohne Doppelversand anzuschreiben.
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
                        <Button onClick={create} data-testid="campaign-create-submit" className="bg-[#007bff] hover:bg-[#0056b3]">Kampagne erstellen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-[#1a2b48]">
                            Vorschau: {selectedCampaign?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {!previewData ? (
                        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
                    ) : previewData.recipients_total === 0 ? (
                        <div className="py-12 text-center">
                            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
                            <p className="text-slate-600">Keine aktiven Empfänger entsprechen den Filterkriterien.</p>
                            <p className="text-xs text-slate-400 mt-2">Bitte zuerst Kontakte hinzufügen oder Filter anpassen.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm">
                                <strong className="text-[#1a2b48]">{previewData.recipients_total}</strong> aktive Empfänger
                                in <strong>{previewData.previews.length}</strong> Sprache(n).
                            </div>

                            <Tabs value={previewLang} onValueChange={setPreviewLang}>
                                <TabsList>
                                    {previewData.previews.map((p) => (
                                        <TabsTrigger key={p.language} value={p.language}>
                                            {p.language} ({p.count})
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                                {previewData.previews.map((p) => (
                                    <TabsContent key={p.language} value={p.language} className="mt-4">
                                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                                            <div className="bg-slate-50 p-3 border-b border-slate-200 text-xs text-slate-500">
                                                <strong className="text-[#1a2b48]">An:</strong> {p.sample_recipient}<br />
                                                <strong className="text-[#1a2b48]">Betreff:</strong> {p.subject}
                                            </div>
                                            <iframe srcDoc={p.html} title={`preview-${p.language}`}
                                                className="w-full border-0 bg-slate-100" style={{ height: 600 }}
                                                data-testid={`campaign-preview-${p.language.toLowerCase()}`} />
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>

                            {(selectedCampaign?.status === "draft" || selectedCampaign?.status === "failed") && (
                                <div className="flex justify-end pt-2">
                                    <Button onClick={() => send(selectedCampaign.id)} className="bg-[#007bff] hover:bg-[#0056b3]" data-testid="preview-send-btn">
                                        <Send className="w-4 h-4 mr-2" />Jetzt an alle {previewData.recipients_total} senden
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
